import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faRotateRight,
  faFileExcel,
  faFilePdf,
  faSearch,
  faClose,
  faEllipsisH,
  faBookOpen,
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import { useDebounce } from "use-debounce";
import { hasPermission } from "../../../utils/permissionUtils";

import Select from "react-select";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import { getAllBranches } from "../../../api/hrm/branches/info";

const monthInputDefault = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`;
};

const BusinessTripFilter = ({
  onNewClick,
  onFilterChange,
  onDownloadCsv,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [loadingManual, setLoadingManual] = useState(false);
  const [monthValue, setMonthValue] = useState(monthInputDefault());

  const [filters, setFilters] = useState({
    keyword: "",
    from_date: "",
    to_date: "",
  });

  const [showExportModal, setShowExportModal] = useState(false);
  const [branchOptions, setBranchOptions] = useState([]);
  const [exportBranchId, setExportBranchId] = useState("");
  const [exportMonth, setExportMonth] = useState(monthInputDefault());

  const [debouncedKeyword] = useDebounce(filters.keyword, 500);

  useEffect(() => {
    if (!filters.from_date || !filters.to_date) return;
    onFilterChange?.({ ...filters, keyword: debouncedKeyword });
  }, [debouncedKeyword]);

  const formatDateLocal = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const monthToDateRange = (monthStr) => {
    const safe = monthStr || monthInputDefault();
    const [yearStr, monthStrNum] = safe.split("-");
    const year = Number(yearStr);
    const month = Number(monthStrNum);
    const first = new Date(year, month - 1, 1);
    const last = new Date(year, month, 0);
    return {
      from_date: formatDateLocal(first),
      to_date: formatDateLocal(last),
    };
  };

  useEffect(() => {
    const initMonth = monthInputDefault();
    const range = monthToDateRange(initMonth);

    const defaults = {
      keyword: "",
      ...range,
    };

    setMonthValue(initMonth);
    setFilters(defaults);
    onFilterChange?.(defaults);
  }, []);

  useEffect(() => {
    getAllBranches()
      .then((res) => {
        const branches = res.items || res || [];
        setBranchOptions(
          branches.map((b) => ({ value: b.id, label: b.name }))
        );
      })
      .catch(console.error);
  }, []);

  const updateFilter = (name, value) => {
    const updated = { ...filters, [name]: value };
    setFilters(updated);
    if (name !== "keyword") onFilterChange?.(updated);
  };

  const handleOpenManual = async () => {
    try {
      setLoadingManual(true);
      const guideUrl = `${window.location.origin}/guides/hrm-business-trip-guide.html`;
      window.open(guideUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      alert("Không thể tải hướng dẫn!");
    } finally {
      setLoadingManual(false);
      setShowMenu(false);
    }
  };

  const handleMonthChange = (nextMonth) => {
    setMonthValue(nextMonth);
    const range = monthToDateRange(nextMonth);
    const updated = { ...filters, ...range };
    setFilters(updated);
    onFilterChange?.(updated);
  };

  const handleRefresh = () => {
    onFilterChange?.({ ...filters });
  };

  return (
    <div className="mb-4 rounded-2xl border border-gray-100 bg-white p-3 md:p-4 shadow-sm relative">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="w-full sm:w-auto">
            <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">
              Tháng
            </label>
            <input
              type="month"
              value={monthValue}
              onChange={(e) => handleMonthChange(e.target.value)}
              className="h-11 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm font-semibold text-gray-700 outline-none transition-all focus:border-red-400 focus:bg-white focus:ring-2 focus:ring-red-100"
            />
          </div>

          <div className="w-full sm:min-w-[320px]">
            <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">
              Tìm kiếm
            </label>
            <div className="relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Tìm theo tên nhân viên"
                value={filters.keyword}
                onChange={(e) => updateFilter("keyword", e.target.value)}
                className="h-11 w-full rounded-xl border border-gray-200 bg-gray-50 pl-10 pr-3 text-sm font-medium text-gray-700 outline-none transition-all focus:border-red-400 focus:bg-white focus:ring-2 focus:ring-red-100"
              />
            </div>
          </div>

          <button
            type="button"
            onClick={handleRefresh}
            className="inline-flex h-11 w-11 min-w-[44px] max-w-[44px] aspect-square shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white p-0 text-gray-600 transition-all hover:border-red-200 hover:bg-red-50 hover:text-red-600"
            title="Làm mới dữ liệu"
          >
            <FontAwesomeIcon icon={faRotateRight} />
          </button>
        </div>

        <div className="flex items-center gap-2">
          {hasPermission("hrm.business_trip", "C") && (
            <button
              type="button"
              onClick={onNewClick}
              className="hidden h-11 items-center gap-2 rounded-xl bg-gradient-to-r from-red-700 to-red-800 px-4 text-sm font-bold text-white shadow-sm transition-all hover:shadow-md md:inline-flex"
            >
              <FontAwesomeIcon icon={faPlus} />
              Đăng ký công tác
            </button>
          )}

          <div className="relative">
            <button
              type="button"
              onClick={() => setShowMenu((prev) => !prev)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 transition-all hover:border-red-200 hover:bg-red-50 hover:text-red-600"
              title="Tùy chọn khác"
            >
              <FontAwesomeIcon icon={faEllipsisH} />
            </button>

            <AnimatePresence>
              {showMenu && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 z-50 mt-2 w-[215px] overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg"
                >
                  <button
                    type="button"
                    onClick={() => {
                      window.open(
                        "/templates/hrm/business_trip/frm_business_trip.docx",
                        "_blank",
                        "noopener,noreferrer"
                      );
                      setShowMenu(false);
                    }}
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-red-50"
                  >
                    <FontAwesomeIcon icon={faFilePdf} className="text-gray-400" />
                    Tải biểu mẫu
                  </button>

                  {hasPermission("hrm.business_trip", "E") && (
                    <button
                      type="button"
                      onClick={() => {
                        setShowMenu(false);
                        setShowExportModal(true);
                      }}
                      className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-red-50"
                    >
                      <FontAwesomeIcon icon={faFileExcel} className="text-gray-400" />
                      Tải file Excel (.xlsx)
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={handleOpenManual}
                    disabled={loadingManual}
                    className={`flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-red-50 ${
                      loadingManual ? "cursor-not-allowed opacity-50" : ""
                    }`}
                  >
                    <FontAwesomeIcon icon={faBookOpen} className="text-gray-400" />
                    {loadingManual ? "Đang mở..." : "Hướng dẫn"}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {hasPermission("hrm.business_trip", "C") && (
        <button
          type="button"
          onClick={onNewClick}
          className="fixed bottom-20 right-5 z-40 inline-flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-red-700 to-red-800 text-xl text-white shadow-lg transition-all hover:scale-105 active:scale-95 md:hidden"
          title="Đăng ký công tác"
        >
          <FontAwesomeIcon icon={faPlus} />
        </button>
      )}

      <AnimatePresence>
        {showExportModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9, y: 40 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b">
                <h2 className="text-lg font-semibold text-gray-800">
                  Xuất Excel
                </h2>
                <button
                  type="button"
                  onClick={() => setShowExportModal(false)}
                  className="hover:bg-gray-100 rounded-full p-2"
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </div>

              <div className="p-6 space-y-4 text-gray-700">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Chi nhánh
                  </label>
                  <Select
                    options={branchOptions}
                    styles={customSelectStyles}
                    value={
                      exportBranchId
                        ? branchOptions.find(
                            (b) => String(b.value) === String(exportBranchId)
                          ) || null
                        : null
                    }
                    onChange={(opt) =>
                      setExportBranchId(opt?.value ? String(opt.value) : "")
                    }
                    placeholder="Tất cả chi nhánh"
                    isClearable
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tháng / năm
                  </label>
                  <input
                    type="month"
                    value={exportMonth}
                    onChange={(e) => setExportMonth(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-red-400 outline-none"
                  />
                </div>
              </div>

              <div className="flex gap-2 mt-2 px-6 py-4 border-t bg-gray-50">
                <button
                  type="button"
                  onClick={() => setShowExportModal(false)}
                  className="flex-1 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const branchLabel =
                      exportBranchId !== ""
                        ? branchOptions.find(
                            (b) =>
                              String(b.value) === String(exportBranchId)
                          )?.label || "Chi nhánh"
                        : "Tất cả chi nhánh";

                    onDownloadCsv?.({
                      branch_id: exportBranchId || undefined,
                      month: exportMonth,
                      branch_label: branchLabel,
                    });
                    setShowExportModal(false);
                  }}
                  className="flex-1 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                >
                  Tải file .xlsx
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BusinessTripFilter;
